import type {PopupAttrs} from '@jetbrains/ring-ui/components/popup/popup'
import classNames from 'classnames'
import * as React from 'react'

import useEventCallback from '../../../../hooks/useEventCallback'
import {getSandbox} from '../../../../utils/fastdom'
import type {VirtualizedTreeNodeProps} from '../VirtualizedTree'
import {useShowPopup} from '../VirtualizedTree.contexts'
import ExpandHandle from '../VirtualizedTreeExpandHandle/VirtualizedTreeExpandHandle'

import {VirtualizedTreeOverlayItemPopup} from './VirtualizedTreeOverlayItemPopup'

import styles from './VirtualizedTreeOverlayItem.css'

const REGULAR_ITEM_HEIGHT = 24
const SIDEBAR_FOOTER_HEIGHT = 41
type Props = VirtualizedTreeNodeProps & {
  expandable?: boolean
  expanded?: boolean
  active?: boolean
  children: React.ReactNode
  className?: string
  wrapperClassName?: string
  hoveredClassName?: string
  focusedClassName?: string
  activeClassName?: string
  wrapperHtmlProps?: React.JSX.IntrinsicElements['div']
  popupHtmlProps?: Partial<PopupAttrs>
  onToggleExpand?: () => unknown
  itemType?: string
  itemKey: string
}
type InnerProps = Props & {
  showPopup: boolean
  setShowPopup: (arg0: boolean) => unknown
}
const VirtualizedTreeOverlayItemInner = React.memo(function VirtualizedTreeOverlayItemInner(
  props: InnerProps,
) {
  const {
    height,
    wrapperHtmlProps,
    popupHtmlProps,
    width,
    style,
    className,
    hoveredClassName,
    focusedClassName = '',
    activeClassName = '',
    wrapperClassName,
    expandable = false,
    expanded = false,
    active = false,
    indent,
    onToggleExpand,
    children,
    itemType,
    showPopup,
    setShowPopup,
  } = props
  const [focused, setFocused] = React.useState(false)

  const openPopup = useEventCallback(() => {
    setShowPopup(true)
  })
  const closePopup = useEventCallback(() => {
    setShowPopup(false)
  })
  const onMouseEnter = useEventCallback(async (event: React.MouseEvent<any>) => {
    const {clientY, target} = event

    if (clientY > window.innerHeight - SIDEBAR_FOOTER_HEIGHT - REGULAR_ITEM_HEIGHT) {
      if (target != null && target instanceof Element) {
        const sandbox = getSandbox(target)
        const fullyFit = await sandbox.measure(
          () => target.getBoundingClientRect().bottom <= window.innerHeight - SIDEBAR_FOOTER_HEIGHT,
        )

        if (fullyFit) {
          sandbox.mutate(() => openPopup())
        }
      }
    } else {
      openPopup()
    }
  })

  const onFocus = useEventCallback(() => setFocused(true))
  const onBlur = useEventCallback((e: React.FocusEvent<HTMLDivElement>) => {
    setFocused(false)

    if (wrapperHtmlProps?.onBlur != null) {
      wrapperHtmlProps.onBlur(e)
    }
  })

  const childClassName = classNames(
    styles.item,
    className,
    {
      [activeClassName]: active,
    },
    {
      [focusedClassName]: focused,
    },
  )
  const popupClassName = classNames(childClassName, hoveredClassName)
  const content = (
    <>
      <ExpandHandle
        className={styles.expandHandle}
        expandable={expandable}
        expanded={expanded}
        indent={indent}
        onClick={onToggleExpand}
        tabIndex={-1}
      />
      <div className={styles.contentWrapper}>{children}</div>
    </>
  )
  return (
    <div
      {...wrapperHtmlProps}
      style={style}
      className={wrapperClassName}
      role="row"
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {showPopup && width != null && (
        <VirtualizedTreeOverlayItemPopup
          className={popupClassName}
          height={height}
          width={width}
          popupHtmlProps={popupHtmlProps}
          onClose={closePopup}
        >
          {content}
        </VirtualizedTreeOverlayItemPopup>
      )}
      <div role="cell" onMouseEnter={onMouseEnter} className={childClassName} data-test={itemType}>
        {content}
      </div>
    </div>
  )
})

export default function VirtualizedTreeOverlayItem(props: Props) {
  const [showPopup, setShowPopup] = useShowPopup(props.itemKey)
  return (
    <VirtualizedTreeOverlayItemInner {...props} showPopup={showPopup} setShowPopup={setShowPopup} />
  )
}
